// VenuesPage: List of venues
import React, { useEffect, useState } from 'react';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, Card, CardContent, Avatar, Chip, Divider, Button, Stack, IconButton, Fade, Container } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import api from '../services/api';

function VenuesPage() {
  const theme = useTheme();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get('/api/venues')
      .then(res => { if (mounted) setVenues(res.data || []); })
      .catch(() => { if (mounted) setVenues([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Carousel state
  const [active, setActive] = useState(0);
  const timerRef = React.useRef();

  // Auto-rotate if more than 1 venue
  useEffect(() => {
    if (venues.length > 1) {
      timerRef.current = setTimeout(() => setActive(a => (a + 1) % venues.length), 5000);
      return () => clearTimeout(timerRef.current);
    }
  }, [active, venues.length]);

  return (
    <PublicLayout>
      <Box sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundImage: venues && venues.length ? `url(${venues[active]?.image || ''})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}>
        <Box sx={{ position: 'relative', py: 6 }}>
          {venues && venues.length && venues[active]?.image && (
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(3px)', zIndex: 0 }} />
          )}
          <Box sx={{ maxWidth: 900, mx: 'auto', mt: 8, mb: 8, p: { xs: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom color="primary" align="center">Venues</Typography>

        {loading ? (
          <Typography align="center">Loading...</Typography>
        ) : venues.length === 0 ? (
          <Typography align="center" color="text.secondary">No venues have been added yet.</Typography>
        ) : venues.length === 1 ? (
          <VenueCard venue={venues[0]} />
        ) : (
          <Box sx={{ position: 'relative', minHeight: 340, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Fade in timeout={500} key={active}>
              <Box>
                <VenueCard venue={venues[active]} />
              </Box>
            </Fade>
            <IconButton onClick={() => setActive(a => (a - 1 + venues.length) % venues.length)} sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 5, bgcolor: 'rgba(0,0,0,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' } }}>
              <ArrowBackIosNewIcon />
            </IconButton>
            <IconButton onClick={() => setActive(a => (a + 1) % venues.length)} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 5, bgcolor: 'rgba(0,0,0,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.35)' } }}>
              <ArrowForwardIosIcon />
            </IconButton>
            <Box sx={{ position: 'absolute', bottom: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1 }}>
              {venues.map((_, idx) => (
                  <Box key={idx} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: idx === active ? 'primary.main' : 'grey.400', opacity: idx === active ? 1 : 0.5, transition: 'all 0.2s' }} />
                ))}
              </Box>
            </Box>
          )}
          </Box>
        </Box>
      </Box>
    </PublicLayout>
  );

  // VenueCard component
  function VenueCard({ venue }) {
    return (
      <Card sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, boxShadow: 6, borderRadius: 3, overflow: 'hidden', minHeight: 320, maxWidth: 800, mx: 'auto', backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.78)', backdropFilter: 'blur(6px)' }}>
        <Box sx={{ flex: '0 0 340px', width: '100%', height: { xs: 220, md: 260 }, overflow: 'hidden', position: 'relative' }}>
          {venue.image ? (
            <img src={venue.image} alt={venue.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.100', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">No image</Typography>
            </Box>
          )}
          <Box sx={{ position: 'absolute', left: 12, top: 12 }}>
            {Array.isArray(venue.tags) && venue.tags.slice(0,2).map(t => (
              <Chip key={t} label={t} size="small" sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.85)' }} />
            ))}
          </Box>
        </Box>
        <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{venue.name}</Typography>
              {venue.location && (
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <LocationOnIcon fontSize="small" />{venue.location}
                </Typography>
              )}
              {venue.capacity && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Capacity: <strong>{venue.capacity}</strong></Typography>}
              {venue.description && <Typography variant="body1" sx={{ mt: 1 }}>{venue.description}</Typography>}
              {Array.isArray(venue.tags) && venue.tags.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {venue.tags.map(t => <Chip key={t} label={t} size="small" />)}
                </Stack>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              {venue.coords && venue.coords.lat && venue.coords.lng && (
                <Button size="small" variant="outlined" endIcon={<OpenInNewIcon />} href={`https://www.google.com/maps/search/?api=1&query=${venue.coords.lat},${venue.coords.lng}`} target="_blank">Open Map</Button>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Added: {new Date(venue.createdAt || venue.updatedAt || Date.now()).toLocaleDateString()}</Typography>
            </Box>
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Venue Staff</Typography>
            {(!venue.staff || venue.staff.length === 0) ? (
              <Typography variant="body2" color="text.secondary">No staff listed.</Typography>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {venue.staff.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                      <Avatar src={s.image} sx={{ width: 48, height: 48 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.role}</Typography>
                        {s.email && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.email}</Typography>}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>
      </Card>
    );
  }
}

export default VenuesPage;
